/**
 * Lightweight player interaction state for backyard toys.
 * Kick + pick-up/carry pinecones. Pattern is intentionally small — not a framework.
 */

import type { SpawnPoint } from '@/world-prototype/types';

export type PlayerInteractionState =
  | 'idle'
  | 'moving'
  | 'approachingInteraction'
  | 'performingInteraction';

export type PlayerInteractionType =
  | 'kickPinecone'
  | 'pickUpPinecone'
  | 'throwPinecone'
  | 'retrieveBucket'
  | 'storeBucket'
  | 'pickUpBucket'
  | 'placeBucket'
  | 'depositPinecone'
  | 'lightFirepit'
  | 'burnPinecone'
  | 'harvestGarden';

/** What Big Sister is currently holding (never both). */
export type CarriedKind = 'pinecone' | 'bucket';

export interface PlayerInteractionStatus {
  state: PlayerInteractionState;
  interactionType: PlayerInteractionType | null;
  targetId: string | null;
}

/** Stop this far from the pinecone center (world-width units) — one kick-foot. */
export const KICK_APPROACH_DISTANCE = 0.036;

/** Pickup stand-off — close enough to reach, not on top of the cone. */
export const PICKUP_APPROACH_DISTANCE = 0.028;

/** Generous touch hit target for pinecone taps (CSS pixels). */
export const PINECONE_HIT_MIN_CSS_PX = 56;

/** Hold duration before a pinecone press becomes pickup (not kick). */
export const PINECONE_LONG_PRESS_MS = 600;

/** Cancel long-press if the pointer drifts more than this (CSS px). */
export const PINECONE_LONG_PRESS_MOVE_CANCEL_PX = 14;

/** Kick choreography timing (ms). */
export const KICK_ANTICIPATION_MS = 70;
export const KICK_CONTACT_MS = 160;
export const KICK_TOTAL_MS = 420;

/** Pickup choreography timing (ms). */
export const PICKUP_ANTICIPATION_MS = 80;
export const PICKUP_CONTACT_MS = 220;
export const PICKUP_TOTAL_MS = 480;

/** Throw: max ground travel in world-width units. */
export const THROW_MAX_DISTANCE = 0.32;

/** Throw duration range (ms) — short toss → longer arc. */
export const THROW_MIN_DURATION_MS = 420;
export const THROW_MAX_DURATION_MS = 720;

/** Peak visual lift in CSS pixels (scaled by throw distance). */
export const THROW_MIN_PEAK_LIFT_PX = 28;
export const THROW_MAX_PEAK_LIFT_PX = 56;

/** Brief landing bounce after arc (ms). */
export const THROW_BOUNCE_MS = 90;

/** Character throw anticipation before release (ms). */
export const THROW_ANTICIPATION_MS = 90;
export const THROW_TOTAL_CHAR_MS = 380;

/** Initial launch speed in world-width units per second. */
export const KICK_SPEED = 0.72;

/** Exponential velocity decay (1/s). Higher = stops sooner. */
export const KICK_FRICTION = 3.4;

/** Stop sliding below this speed. */
export const KICK_STOP_SPEED = 0.02;

/** Optional tumble rate (deg/s) scaled by launch strength. */
export const KICK_TUMBLE_DEG_PER_SEC = 420;

/** Travel target ≈ several character widths (character ~0.055 world width). */
export const KICK_CHARACTER_WIDTH = 0.042;

/**
 * Local carry anchor relative to Big Sister's feet (world-width / world-y).
 * Positive X is toward her facing direction; negative Y is up the screen.
 */
export const CARRY_ANCHOR = {
  x: 0.015,
  y: -0.042,
} as const;

/** Bucket hangs at her side / hip, readable in front of her body. */
export const BUCKET_CARRY_ANCHOR = {
  x: 0.024,
  y: -0.028,
} as const;

/** Generous touch target for the bucket (CSS pixels) — independent of visual size. */
export const BUCKET_HIT_MIN_CSS_PX = 68;

/** Generous touch target for the garden bed (CSS pixels) — covers the visible planter. */
export const GARDEN_HIT_MIN_CSS_PX = 76;

/** Stand-off when interacting with the bucket / shed door. */
export const BUCKET_APPROACH_DISTANCE = 0.042;

/** Shed door open / close timing (ms) — atomic retrieve/store sequence. */
export const SHED_DOOR_OPEN_MS = 320;
export const SHED_BUCKET_REVEAL_MS = 320;
export const SHED_RETRIEVE_ATTACH_MS = 280;
export const SHED_DOOR_CLOSE_MS = 300;
export const SHED_STORE_PUT_MS = 280;

/** @deprecated kept for any lingering imports — prefer sequenced constants above. */
export const SHED_DOOR_HOLD_MS = 420;
export const SHED_RETRIEVE_MS = 1400;
export const SHED_STORE_MS = 1200;

/** Shed interaction sequence lock (one tap → full retrieve/store/return). */
export type ShedSequenceState =
  | 'idle'
  | 'walking'
  | 'walkingToBucket'
  | 'pickingUpBucket'
  | 'walkingToShed'
  | 'opening'
  | 'revealing'
  | 'retrieving'
  | 'storing'
  | 'closing';

/** Bucket pickup / place choreography (ms). */
export const BUCKET_PICKUP_CONTACT_MS = 200;
export const BUCKET_PICKUP_TOTAL_MS = 460;
export const BUCKET_PLACE_TOTAL_MS = 380;
export const DEPOSIT_PINECONE_MS = 360;

export const BUCKET_OBJECT_ID = 'bucket';
export const SHED_OBJECT_ID = 'shed';
export const FIRE_PIT_OBJECT_ID = 'fire-pit';

/** Stand-off when lighting the fire or feeding it a pinecone. */
export const FIRE_PIT_APPROACH_DISTANCE = 0.038;

/** Beat while the character crouches to light the fire (ms). */
export const LIGHT_FIRE_MS = 420;

/** Pinecone-to-fire deposit choreography — mirrors bucket deposit timing. */
export const BURN_PINECONE_MS = 360;

/** How long the flare-up stays enlarged before settling (ms). */
export const FIRE_FLARE_MS = 1_000;

export function isKickablePinecone(objectId: string): boolean {
  // Matches `pinecone-1`…`pinecone-10` and any legacy `pinecones-*` ids.
  return objectId.startsWith('pinecone');
}

export function isBucketObject(objectId: string): boolean {
  return objectId === BUCKET_OBJECT_ID;
}

export function isShedObject(objectId: string): boolean {
  return objectId === SHED_OBJECT_ID;
}

export function isFirePitObject(objectId: string): boolean {
  return objectId === FIRE_PIT_OBJECT_ID;
}

/** Lightweight per-instance pinecone toy state (authored placement stays separate). */
export type PineconeToyState =
  | 'ground'
  | 'carried'
  | 'thrown'
  | 'bucket'
  | 'burned';

export interface PineconeInstanceRuntime {
  id: string;
  x: number;
  y: number;
  rotation: number;
  state: PineconeToyState;
  /** When state is `bucket`, which container holds this instance. */
  containerId?: string | null;
  hitOffsetX: number;
  hitOffsetY: number;
  standOffsetX: number;
  standOffsetY: number;
  width: number;
  height: number;
}

/** Bucket lifecycle — authored editor pose stays separate from runtime. */
export type BucketToyState = 'storedInShed' | 'ground' | 'carried';

export interface BucketInstanceRuntime {
  id: string;
  state: BucketToyState;
  /** Current gameplay pose (placement / carry follow). */
  x: number;
  y: number;
  rotation: number;
  /** Authored World Editor default — never overwritten by placement. */
  authoredX: number;
  authoredY: number;
  authoredRotation: number;
  /** Individual pinecone ids currently inside (order = deposit order). */
  contents: string[];
  hitOffsetX: number;
  hitOffsetY: number;
  standOffsetX: number;
  standOffsetY: number;
  width: number;
  height: number;
}

/** World pose for a carried object attached to the player. */
export function carriedObjectWorldPose(
  character: SpawnPoint,
  facing: 'left' | 'right',
  anchor: { x: number; y: number } = CARRY_ANCHOR,
): SpawnPoint {
  const side = facing === 'left' ? -1 : 1;
  return {
    x: character.x + side * anchor.x,
    y: character.y + anchor.y,
  };
}

export interface PineconeThrowPlan {
  from: SpawnPoint;
  land: SpawnPoint;
  durationMs: number;
  peakLiftPx: number;
  tumbleDegPerSec: number;
  rotation0: number;
}

/**
 * Resolve a throw from release pose toward a desired world tap.
 * Clamps to playable area and caps max distance.
 */
export function createPineconeThrowPlan(
  from: SpawnPoint,
  desired: SpawnPoint,
  worldAspect: number,
  clampPoint: (x: number, y: number) => SpawnPoint,
  random: () => number = Math.random,
): PineconeThrowPlan {
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const clampedDesired = clampPoint(desired.x, desired.y);
  let dx = clampedDesired.x - from.x;
  let dy = (clampedDesired.y - from.y) / aspect;
  let dist = Math.hypot(dx, dy);
  if (dist < 0.0001) {
    dx = 0.04;
    dy = 0;
    dist = 0.04;
  }
  const ux = dx / dist;
  const uy = dy / dist;
  const travel = Math.min(dist, THROW_MAX_DISTANCE);
  const rawLand = {
    x: from.x + ux * travel,
    y: from.y + uy * travel * aspect,
  };
  const land = clampPoint(rawLand.x, rawLand.y);
  const finalDist = Math.hypot(
    land.x - from.x,
    (land.y - from.y) / aspect,
  );
  const t = Math.min(1, Math.max(0, finalDist / THROW_MAX_DISTANCE));
  const durationMs = Math.round(
    THROW_MIN_DURATION_MS + t * (THROW_MAX_DURATION_MS - THROW_MIN_DURATION_MS),
  );
  const peakLiftPx =
    THROW_MIN_PEAK_LIFT_PX +
    t * (THROW_MAX_PEAK_LIFT_PX - THROW_MIN_PEAK_LIFT_PX);
  const tumbleSign = random() < 0.5 ? -1 : 1;
  return {
    from: { ...from },
    land,
    durationMs,
    peakLiftPx: peakLiftPx * (0.92 + random() * 0.16),
    tumbleDegPerSec: tumbleSign * (480 + random() * 220),
    rotation0: (random() - 0.5) * 40,
  };
}

export interface PineconeThrowSample {
  x: number;
  y: number;
  /** CSS px upward lift (0 on ground). */
  liftPx: number;
  rotationDeg: number;
  /** 0 = just released, 1 = at landing. */
  t: number;
  landed: boolean;
}

/** Sample airborne throw path. `elapsedMs` from release. */
export function samplePineconeThrow(
  plan: PineconeThrowPlan,
  elapsedMs: number,
): PineconeThrowSample {
  const t = Math.min(1, Math.max(0, elapsedMs / Math.max(1, plan.durationMs)));
  // Ease-out ground travel so landing reads clearly.
  const u = 1 - (1 - t) * (1 - t);
  const x = plan.from.x + (plan.land.x - plan.from.x) * u;
  const y = plan.from.y + (plan.land.y - plan.from.y) * u;
  const liftPx = Math.sin(Math.PI * t) * plan.peakLiftPx;
  const rotationDeg =
    plan.rotation0 + plan.tumbleDegPerSec * (elapsedMs / 1000);
  return {
    x,
    y,
    liftPx,
    rotationDeg,
    t,
    landed: t >= 1,
  };
}

/**
 * Stand point for a kick/pickup: short of the target along the approach vector.
 * Y uses world-aspect correction so distance feels circular on screen.
 */
export function kickApproachPoint(
  from: SpawnPoint,
  target: SpawnPoint,
  distance: number,
  worldAspect: number,
): SpawnPoint {
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const dx = target.x - from.x;
  const dy = (target.y - from.y) / aspect;
  const len = Math.hypot(dx, dy);
  if (len < 0.0001) {
    return { x: target.x - distance, y: target.y };
  }
  const ux = dx / len;
  const uy = dy / len;
  return {
    x: target.x - ux * distance,
    y: target.y - uy * distance * aspect,
  };
}

export interface PineconeKickLaunch {
  vx: number;
  vy: number;
  tumbleDegPerSec: number;
}

/** Velocity away from kicker along BigSister → pinecone, with small variation. */
export function createPineconeKickLaunch(
  from: SpawnPoint,
  pinecone: SpawnPoint,
  worldAspect: number,
  random: () => number = Math.random,
): PineconeKickLaunch {
  const aspect = worldAspect > 0 ? worldAspect : 1;
  let dx = pinecone.x - from.x;
  let dy = (pinecone.y - from.y) / aspect;
  let len = Math.hypot(dx, dy);
  if (len < 0.0001) {
    dx = 1;
    dy = 0;
    len = 1;
  }
  const ux = dx / len;
  const uy = dy / len;
  // Small angle jitter (~±12°) and strength variation.
  const angleJitter = (random() - 0.5) * 0.42;
  const cos = Math.cos(angleJitter);
  const sin = Math.sin(angleJitter);
  const jx = ux * cos - uy * sin;
  const jy = ux * sin + uy * cos;
  const strength = KICK_SPEED * (0.88 + random() * 0.28);
  const tumbleSign = random() < 0.5 ? -1 : 1;
  return {
    vx: jx * strength,
    vy: jy * strength * aspect,
    tumbleDegPerSec:
      tumbleSign * KICK_TUMBLE_DEG_PER_SEC * (0.75 + random() * 0.5),
  };
}

export interface PineconeMotionState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotationDeg: number;
  tumbleDegPerSec: number;
  active: boolean;
}

/**
 * Integrate one frame of pinecone slide. Stays inside walk polygon / bounds
 * via the provided clamp. Gentle bounce when the clamp moves the point.
 */
export function stepPineconeMotion(
  state: PineconeMotionState,
  dt: number,
  clampPoint: (x: number, y: number) => SpawnPoint,
): PineconeMotionState {
  if (!state.active || dt <= 0) return state;

  const decay = Math.exp(-KICK_FRICTION * dt);
  let vx = state.vx * decay;
  let vy = state.vy * decay;
  const speed = Math.hypot(vx, vy);
  if (speed < KICK_STOP_SPEED) {
    return {
      ...state,
      vx: 0,
      vy: 0,
      tumbleDegPerSec: 0,
      active: false,
    };
  }

  const nextX = state.x + vx * dt;
  const nextY = state.y + vy * dt;
  const clamped = clampPoint(nextX, nextY);
  // Gentle bounce if we hit the playable edge.
  if (Math.abs(clamped.x - nextX) > 0.0002) vx *= -0.35;
  if (Math.abs(clamped.y - nextY) > 0.0002) vy *= -0.35;

  return {
    x: clamped.x,
    y: clamped.y,
    vx,
    vy,
    rotationDeg: state.rotationDeg + state.tumbleDegPerSec * dt,
    tumbleDegPerSec: state.tumbleDegPerSec * decay,
    active: true,
  };
}

/** Exactly one carried world object at a time (pinecone OR bucket — never both). */
export class CarriedObjectSlot {
  private carriedId: string | null = null;
  private kind: CarriedKind | null = null;

  getId(): string | null {
    return this.carriedId;
  }

  getKind(): CarriedKind | null {
    return this.kind;
  }

  isCarrying(): boolean {
    return this.carriedId != null;
  }

  isCarryingId(id: string): boolean {
    return this.carriedId === id;
  }

  isCarryingPinecone(): boolean {
    return this.kind === 'pinecone';
  }

  isCarryingBucket(): boolean {
    return this.kind === 'bucket';
  }

  /** Returns false if already carrying something else. */
  tryCarry(id: string, kind: CarriedKind = 'pinecone'): boolean {
    if (this.carriedId != null && this.carriedId !== id) return false;
    this.carriedId = id;
    this.kind = kind;
    return true;
  }

  clear(): void {
    this.carriedId = null;
    this.kind = null;
  }
}

export class PlayerInteractionController {
  private state: PlayerInteractionState = 'idle';
  private interactionType: PlayerInteractionType | null = null;
  private targetId: string | null = null;

  getStatus(): PlayerInteractionStatus {
    return {
      state: this.state,
      interactionType: this.interactionType,
      targetId: this.targetId,
    };
  }

  /** Sync with presence walking when no interaction is in flight. */
  noteMoving(walking: boolean): void {
    if (this.state === 'performingInteraction') {
      return;
    }
    if (this.state === 'approachingInteraction') {
      if (!walking) {
        // finishWalk emits before onArrive — defer so perform can claim state.
        queueMicrotask(() => {
          if (this.state === 'approachingInteraction') {
            this.state = 'idle';
            this.interactionType = null;
            this.targetId = null;
          }
        });
      }
      return;
    }
    this.state = walking ? 'moving' : 'idle';
    this.interactionType = null;
    this.targetId = null;
  }

  beginApproach(type: PlayerInteractionType, targetId: string): void {
    this.state = 'approachingInteraction';
    this.interactionType = type;
    this.targetId = targetId;
  }

  beginPerform(): void {
    if (this.state !== 'approachingInteraction') return;
    this.state = 'performingInteraction';
  }

  /** Start a perform without approach (e.g. throw from current stance). */
  beginPerformDirect(type: PlayerInteractionType, targetId: string): void {
    this.state = 'performingInteraction';
    this.interactionType = type;
    this.targetId = targetId;
  }

  /** Cancel approach (e.g. ground tap). Safe during perform — no-op. */
  cancelApproach(): void {
    if (this.state !== 'approachingInteraction') return;
    this.state = 'idle';
    this.interactionType = null;
    this.targetId = null;
  }

  complete(): void {
    this.state = 'idle';
    this.interactionType = null;
    this.targetId = null;
  }

  isPerforming(): boolean {
    return this.state === 'performingInteraction';
  }

  isApproaching(): boolean {
    return this.state === 'approachingInteraction';
  }

  reset(): void {
    this.state = 'idle';
    this.interactionType = null;
    this.targetId = null;
  }
}
